import type { Input, Output } from 'webmidi'

import {
  assertMidiChannel,
  makeDx7BankPayload,
  makeDx7SingleVoicePayload,
  yamahaManufacturerId,
  type Dx7Voice,
} from '@/lib/dx7'
import type { EffectControllerBase } from '@/lib/firmware-profile'
import { fm1EffectParameterMaximums, fm1EffectParameterCount } from '@/lib/fm1-effects'
import { fm1VoiceParameterMaximums } from '@/lib/fm1-parameters'
import { createId } from '@/lib/id'
import { midiActivity } from '@/lib/midi-activity'
import { defaultNoteVelocity } from '@/lib/note-velocity'

export type MidiPort = Input | Output

export type MidiDevice<TPort extends MidiPort> = {
  id: string
  name: string
  manufacturer: string
  state: TPort['state']
  port: TPort
}

export type MidiLogEntry = {
  id: string
  direction: 'in' | 'out' | 'system'
  message: string
  /** When the entry was logged, in milliseconds since the epoch; shown in the interface language. */
  createdAt: number
  data?: Uint8Array
}

export function getMidiSupport() {
  if (!window.isSecureContext) {
    return 'insecure'
  }

  if (!navigator.requestMIDIAccess) {
    return 'unsupported'
  }

  return 'supported'
}

export function portsToDevices<TPort extends MidiPort>(ports: TPort[]) {
  return ports.map((port) => ({
    id: port.id,
    name: port.name ?? 'Unnamed MIDI device',
    manufacturer: port.manufacturer ?? 'Unknown maker',
    state: port.state,
    port,
  }))
}

/**
 * Ports every system of that kind lists whether or not an instrument is attached: Linux's ALSA
 * loopback, which Chrome usually lists first, and the Windows built-in software synth. Choosing
 * one automatically sends everything meant for the FM1 nowhere, with no error to explain why.
 */
const builtInMidiPortNames = [/^midi through\b/i, /^microsoft gs wavetable synth\b/i]

function isBuiltInMidiPort(name: string) {
  return builtInMidiPortNames.some((pattern) => pattern.test(name.trim()))
}

/**
 * The FM1 names its port after itself on Linux (`FM-1 MIDI 1`). macOS lists it as the generic
 * `USB Composite Device`, so a name match is a preference, never a requirement. M-VAVE's SMK-37
 * keyboard lists `SMK-37 Pro-Private`, `SMK-37 Pro-Master`, and `Port 3` on macOS, and only
 * `Private` answers the identity query (docs/hardware-runs/smk37-2026-11-03.md), so among the
 * instrument's ports the one named Private comes first.
 */
const instrumentPortName = /\bfm-?1\b|smk/i

type MidiPortChoice = { id: string; name: string }

/**
 * Picks the port to use after the device list is read. When MIDI connects, a remembered port that
 * is missing gives way to the first one available, preferring a port named for the FM1 or the
 * SMK-37, its Private port first, then any real device over a built-in loopback or software synth. Once a port is in use it is never
 * swapped for another device when it disappears, because notes, sounds, and banks meant for the
 * FM1 could then reach a different instrument; nothing is selected until that port returns or
 * another is chosen.
 */
export function resolveMidiPortSelection(
  ports: readonly MidiPortChoice[],
  chosenId: string,
  reason: 'changed' | 'connected',
) {
  if (ports.some((port) => port.id === chosenId)) return chosenId
  if (reason === 'changed' && chosenId) return ''
  const instrumentPorts = ports.filter((port) => instrumentPortName.test(port.name))
  const automaticChoice =
    instrumentPorts.find((port) => /private/i.test(port.name)) ??
    instrumentPorts[0] ??
    ports.find((port) => !isBuiltInMidiPort(port.name)) ??
    ports[0]
  return automaticChoice?.id ?? ''
}

export function sendDx7Voice(output: Output, channel: number, voice: Dx7Voice) {
  output.sendSysex(yamahaManufacturerId, makeDx7SingleVoicePayload(voice, channel))
  midiActivity.signal('out')
}

export function sendDx7Bank(output: Output, channel: number, voices: Dx7Voice[]) {
  output.sendSysex(yamahaManufacturerId, makeDx7BankPayload(voices, channel))
  midiActivity.signal('out')
}

function assertFm1ProgramChange(program: number, channel: number) {
  if (!Number.isInteger(program) || program < 0 || program > 127) {
    throw new RangeError('FM1 program must be an integer from 0 to 127.')
  }
  assertMidiChannel(channel)
}

export function makeFm1ProgramChangeMessage(program: number, channel = 1) {
  assertFm1ProgramChange(program, channel)
  return Uint8Array.from([0xc0 | ((channel - 1) & 0x0f), program])
}

export function sendFm1ProgramChange(output: Output, channel: number, program: number) {
  assertFm1ProgramChange(program, channel)
  output.sendProgramChange(program, { channels: channel })
  midiActivity.signal('out')
}

/**
 * FM1/DX7 single-parameter write payload, excluding F0/43 and F7.
 * The complete message is F0 43 10 pp qq vv F7.
 */
export function makeFm1ParameterPayload(parameter: number, value: number) {
  if (
    !Number.isInteger(parameter) ||
    parameter < 0 ||
    parameter >= fm1VoiceParameterMaximums.length
  ) {
    throw new RangeError(
      `FM1 voice parameter must be an integer from 0 to ${fm1VoiceParameterMaximums.length - 1}.`,
    )
  }
  if (!Number.isInteger(value) || value < 0 || value > fm1VoiceParameterMaximums[parameter]) {
    throw new RangeError(
      `FM1 voice parameter ${parameter} value must be an integer from 0 to ${fm1VoiceParameterMaximums[parameter]}.`,
    )
  }
  return Uint8Array.from([0x10, Math.floor(parameter / 128), parameter % 128, value])
}

export function sendFm1Parameter(output: Output, parameter: number, value: number) {
  output.sendSysex(yamahaManufacturerId, makeFm1ParameterPayload(parameter, value))
  midiActivity.signal('out')
}

/**
 * Checks effect parameter `parameter`, 0 to 23 in the editor's order, and its value, and gives the
 * controller it goes as: `base` (the firmware profile's `effectControllerBase`) plus `parameter`.
 */
function resolveFm1EffectControl(
  parameter: number,
  value: number,
  channel: number,
  base: EffectControllerBase,
) {
  if (!Number.isInteger(parameter) || parameter < 0 || parameter >= fm1EffectParameterCount) {
    throw new RangeError('FM1 effect parameter must be an integer from 0 to 23.')
  }
  if (!Number.isInteger(value) || value < 0 || value > fm1EffectParameterMaximums[parameter]) {
    throw new RangeError(
      `FM1 effect parameter ${parameter} value must be an integer from 0 to ${fm1EffectParameterMaximums[parameter]}.`,
    )
  }
  if (base !== 0 && base !== 70) {
    throw new RangeError('FM1 effect controllers start at CC 0 or CC 70.')
  }
  assertMidiChannel(channel)
  return base + parameter
}

export function makeFm1EffectControlMessage(
  parameter: number,
  value: number,
  channel = 2,
  base: EffectControllerBase = 0,
) {
  const controller = resolveFm1EffectControl(parameter, value, channel, base)
  return Uint8Array.from([0xb0 | ((channel - 1) & 0x0f), controller, value])
}

export function sendFm1EffectControl(
  output: Output,
  channel: number,
  parameter: number,
  value: number,
  base: EffectControllerBase = 0,
) {
  const controller = resolveFm1EffectControl(parameter, value, channel, base)
  output.sendControlChange(controller, value, { channels: channel })
  midiActivity.signal('out')
}

/**
 * M-VAVE's V16 effect controllers (docs/fm1-research.md, "M-VAVE's V16 stock firmware"): four for
 * each effect on the FX channel, from Filter's 70 to Phaser's 93. The development FX probe's V16
 * research mode sends every value from 0 to 127 to them, to check where the FM1 clamps each.
 */
export const fm1V16EffectControllerGroups = [
  { effect: 'Filter', first: 70 },
  { effect: 'Reverb', first: 74 },
  { effect: 'Delay', first: 78 },
  { effect: 'Distortion', first: 82 },
  { effect: 'Chorus', first: 86 },
  { effect: 'Phaser', first: 90 },
] as const

const firstV16EffectController = 70
const lastV16EffectController = 93

/**
 * How the development FX probe chooses what it may send: `profile` sends only the controllers the
 * firmware profile places the effects at, CC 0–23 or V16's 70–93, and only where it offers
 * `effectControllers`; `v16-research` sends CC 0–23 or 70–93 whatever the FM1 runs, for the V16
 * hardware run (docs/hardware-runs/stock-v16-run-sheet.md).
 */
export type Fm1EffectProbeMode = 'profile' | 'v16-research'

/** Whether `controller` is one of V16's effect controllers. */
function isFm1V16EffectController(controller: number) {
  return controller >= firstV16EffectController && controller <= lastV16EffectController
}

function assertFm1EffectDiagnosticControl(controller: number, value: number, channel: number) {
  const known =
    Number.isInteger(controller) &&
    ((controller >= 0 && controller < fm1EffectParameterCount) ||
      isFm1V16EffectController(controller))
  if (!known) {
    throw new RangeError(
      'FM1 effect diagnostic controller must be an integer from 0 to 23 or from 70 to 93.',
    )
  }
  if (!Number.isInteger(value) || value < 0 || value > 127) {
    throw new RangeError('FM1 effect diagnostic value must be an integer from 0 to 127.')
  }
  assertMidiChannel(channel)
}

/**
 * Development-only hardware-research probe for the known FM1 FX CC blocks: V15's CC 0–23 and, by
 * its release notes, V16's CC 70–93.
 *
 * Unlike normal editor writes, this deliberately permits every MIDI 7-bit value so a hardware
 * test can establish whether a historical editor maximum is accepted or clamped by the device.
 * Production UI must not call this function.
 */
export function makeFm1EffectDiagnosticControlMessage(
  controller: number,
  value: number,
  channel = 2,
) {
  assertFm1EffectDiagnosticControl(controller, value, channel)
  return Uint8Array.from([0xb0 | ((channel - 1) & 0x0f), controller, value])
}

export function sendFm1EffectDiagnosticControl(
  output: Output,
  channel: number,
  controller: number,
  value: number,
) {
  assertFm1EffectDiagnosticControl(controller, value, channel)
  output.sendControlChange(controller, value, { channels: channel })
  midiActivity.signal('out')
}

function assertMidiNote(note: number, velocity: number, channel: number) {
  if (!Number.isInteger(note) || note < 0 || note > 127) {
    throw new RangeError('MIDI note must be an integer from 0 to 127.')
  }
  if (!Number.isInteger(velocity) || velocity < 0 || velocity > 127) {
    throw new RangeError('MIDI velocity must be an integer from 0 to 127.')
  }
  assertMidiChannel(channel)
}

export function sendNoteOn(
  output: Output,
  channel: number,
  note: number,
  velocity = defaultNoteVelocity,
) {
  assertMidiNote(note, velocity, channel)
  output.sendNoteOn(note, { channels: channel, rawAttack: velocity })
  midiActivity.signal('out')
}

export function sendNoteOff(output: Output, channel: number, note: number, velocity = 0) {
  assertMidiNote(note, velocity, channel)
  output.sendNoteOff(note, { channels: channel, rawRelease: velocity })
  midiActivity.signal('out')
}

export const midiNoteCount = 128

/**
 * Releases every note on `channel` with a Note Off for each of the 128 notes. The FM1 takes Control
 * Change only for its effect controllers, 0 to 23 on the effect channel, so the standard All Notes
 * Off (CC 123) and All Sound Off (CC 120) never reach its voices.
 */
export function sendEveryNoteOff(output: Output, channel: number) {
  assertMidiChannel(channel)
  for (let note = 0; note < midiNoteCount; note += 1) {
    output.sendNoteOff(note, { channels: channel, rawRelease: 0 })
  }
  midiActivity.signal('out')
}

const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']

const midiTimingClockStatus = 0xf8
const midiActiveSensingStatus = 0xfe

/**
 * Timing clock and active sensing repeat continuously while a sequencer or keyboard is connected.
 * Logging them would fill the monitor many times a second and hide the messages a user is reading.
 * Other system real-time messages are rare and stay visible.
 */
export function isHighRateMidiMessage(data: Uint8Array | number[]) {
  return data[0] === midiTimingClockStatus || data[0] === midiActiveSensingStatus
}

export function formatMidiBytes(data: Uint8Array | number[]) {
  const bytes = Array.from(data)
  const [status, note, velocity] = bytes
  const messageType = status & 0xf0

  if (
    bytes.length >= 3 &&
    note >= 0 &&
    note <= 127 &&
    velocity >= 0 &&
    velocity <= 127 &&
    (messageType === 0x80 || messageType === 0x90)
  ) {
    const channel = (status & 0x0f) + 1
    const noteName = `${noteNames[note % 12]}${Math.floor(note / 12) - 1}`
    const isNoteOff = messageType === 0x80 || velocity === 0

    return `Ch ${channel} Note ${isNoteOff ? 'Off' : 'On'}: ${noteName} (velocity ${velocity})`
  }

  return bytes.map((byte) => byte.toString(16).padStart(2, '0').toUpperCase()).join(' ')
}

export function makeLogEntry(
  direction: MidiLogEntry['direction'],
  message: string,
  data?: Uint8Array | number[],
): MidiLogEntry {
  return {
    id: createId(),
    direction,
    message,
    data: data ? Uint8Array.from(data) : undefined,
    createdAt: Date.now(),
  }
}
